import { type FC, useEffect, useState } from 'react'

import { useSetFocus } from '@please/lrud'
import type { MoveEvent } from '@please/lrud/dist/types'
import { Element, scroller } from 'react-scroll'

import { OptaTeamStanding } from '@sportworld/js-sdk/src/types/soccerDataTypes'
import { getLeaguePositionColor } from '@sportworld/js-sdk/src/utils/soccerData'

import {
  focusIds,
  STANDINGS_SCROLL_OPTIONS,
} from '@/components/DataWidget/constants'
import { orientation } from '@/constants/focus'
import { useDataWidgetTeamId } from '@/hooks'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'

import { TableRow } from './TableRow'
import { STANDINGS_LEAGUE_COLUMNS } from '../config'
import { renderTableHeader } from '../partials'
import {
  StandingsLeagueColumn,
  StandingsLeagueHeaderColumn,
  StandingsLeagueHeaderRow,
  StandingsLeagueScrollContainer,
  StandingsLeagueTable,
} from '../styles'
import type { StandingsComponentProps } from '../types'

export const StandingsLeague: FC<
  StandingsComponentProps & { isPopup: boolean }
> = ({ isPopup, onClickTeam }) => {
  const { t } = useCommonTranslation()
  const { pagePopup, clearPagePopupOptions } = useStore((state) => state.popup)
  const [tableFocusChild, setTableFocusChild] = useState(0)
  const [columnFocusChild, setColumnFocusChild] = useState(0)

  const { division: divisionPage, divisionPopup } = useStore(
    (state) => state.soccerData,
  )

  const setFocus = useSetFocus()
  const teamId = useDataWidgetTeamId()

  const focusIdPrefix = isPopup ? focusIds.pagePopup : ''
  const currentDivision = isPopup ? divisionPopup.division : divisionPage
  const standings = currentDivision[0]?.ranking || []

  const splitIndex = Math.ceil(standings.length / 2)

  const tableData = [
    standings.slice(0, splitIndex),
    standings.slice(splitIndex),
  ]

  const scrollToAnchorTeam = (selectedTeamId: string) => {
    let rowIndex
    const columnIndex = tableData.findIndex((data) => {
      const index = data.findIndex((team: any) =>
        team.contestantId.includes(selectedTeamId),
      )
      if (index > -1) {
        rowIndex = index
        return true
      }
      return false
    })
    scroller.scrollTo(focusIdPrefix + selectedTeamId, STANDINGS_SCROLL_OPTIONS)
    columnIndex > -1 && setTableFocusChild(columnIndex)
    setColumnFocusChild(rowIndex ?? 0)
  }

  useEffect(() => {
    const selectedTeamId = pagePopup?.pageOptions?.selectedTeamId
    if (isPopup && selectedTeamId && currentDivision.length) {
      setFocus(focusIdPrefix + selectedTeamId)
      scrollToAnchorTeam(selectedTeamId)
      clearPagePopupOptions()
    }
  }, [currentDivision.length])

  const renderColumn = (data: OptaTeamStanding[], index: number) =>
    data?.map((team, idx) => {
      const focusId = focusIdPrefix + team.contestantId
      const color = getLeaguePositionColor(idx + splitIndex * index)
      return (
        <Element key={focusId} name={focusId}>
          <TableRow
            focusId={focusId}
            key={focusId}
            columns={STANDINGS_LEAGUE_COLUMNS}
            team={team}
            onClick={onClickTeam}
            highlightColor={color}
            firstColHighlighted
            rowHighlighted={teamId === team.contestantId}
          />
        </Element>
      )
    })

  const handleTableMove = ({ nextChildIndex }: MoveEvent) => {
    setTableFocusChild(nextChildIndex)
  }

  const handleColumnMove = (e: MoveEvent) => {
    setColumnFocusChild(e.nextChildIndex)
    const focusId = e.nextChildNode.focusId
    scroller.scrollTo(focusId, STANDINGS_SCROLL_OPTIONS)
  }

  return (
    <StandingsLeagueTable
      onMove={handleTableMove}
      defaultFocusChild={tableFocusChild}
    >
      <StandingsLeagueHeaderRow>
        {tableData.map((_, index) => (
          <StandingsLeagueHeaderColumn
            key={focusIdPrefix + `StandingsLeagueHeader_${index}`}
          >
            {renderTableHeader(STANDINGS_LEAGUE_COLUMNS, t)}
          </StandingsLeagueHeaderColumn>
        ))}
      </StandingsLeagueHeaderRow>
      <StandingsLeagueScrollContainer
        height={STANDINGS_SCROLL_OPTIONS.height}
        id={focusIds.standingsLeagueContainer}
      >
        {tableData.map((data, index) => (
          <StandingsLeagueColumn
            key={focusIdPrefix + `DataWidgetTable_${index}`}
            orientation={orientation.VERTICAL}
            onMove={handleColumnMove}
            defaultFocusChild={columnFocusChild}
          >
            {renderColumn(data, index)}
          </StandingsLeagueColumn>
        ))}
      </StandingsLeagueScrollContainer>
    </StandingsLeagueTable>
  )
}
